// src/store/jobs/reducer.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Attachment } from '../../models/Attachment.interface';
import { Job }        from '../../models/Job.interface';

interface JobsState {
  jobs: Job[];
  loading: boolean;
  error: string | null;
}

const initialState: JobsState = {
  jobs: [],
  loading: false,
  error: null,
};

// Helper to ensure dates are stored as strings
const normalizeJob = (job: Job): Job => {
  let dtCreatedValue: string;
  
  if (typeof job.dtCreated === 'string') {
    dtCreatedValue = job.dtCreated;
  } else {
    // For all other cases, just use current date
    dtCreatedValue = new Date().toISOString();
    
    // Try to convert if it seems to be a Date-like object
    try {
      if (job.dtCreated && typeof job.dtCreated === 'object') {
        // Use type assertion to let TypeScript know we're aware of what we're doing
        const dateObj = job.dtCreated as unknown as { toISOString?: () => string };
        if (dateObj.toISOString) {
          dtCreatedValue = dateObj.toISOString();
        }
      }
    } catch (e) {
      console.warn('Error converting dtCreated to ISO string:', e);
      // Keep the default value we set above
    }
  }
  
  return {
    ...job,
    dtCreated: dtCreatedValue
  };
};

const jobsSlice = createSlice({
  name: "jobs",
  initialState,
  reducers: {
    setJobs(state, action: PayloadAction<Job[]>) {
      // Ensure all jobs have proper date serialization
      state.jobs = action.payload.map(job => normalizeJob(job));
      state.loading = false;
      state.error = null;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.loading = false;
    },
    addJob(state, action: PayloadAction<Job>) {
      state.jobs.push(normalizeJob(action.payload));
    },
    updateJob(state, action: PayloadAction<Job>) {
      const index = state.jobs.findIndex(job => job.id === action.payload.id);
      if (index !== -1) {
        state.jobs[index] = normalizeJob(action.payload);
      }
    },
    removeJob(state, action: PayloadAction<string>) {
      state.jobs = state.jobs.filter(job => job.id !== action.payload);
    }
  }
});

export const {
  setJobs,
  setLoading,
  setError,
  addJob,
  updateJob,
  removeJob
} = jobsSlice.actions;

export default jobsSlice.reducer;